import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { checkCustomerEmail } from '../lib/customerApi';
import { useAuth } from '../context/AuthContext';
import './auth.css';

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/profile';
  const {
    isAuthenticated,
    loginWithEmail,
    createAccountWithEmail,
    loginWithGoogle,
  } = useAuth();

  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountExists, setAccountExists] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const handleEmailContinue = async (event) => {
    event.preventDefault();
    setError('');

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError('Please enter your email address.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await checkCustomerEmail(normalizedEmail);
      setAccountExists(!!result.exists);
      setEmail(normalizedEmail);
      setStep('password');
    } catch (err) {
      setError(err.message || 'Unable to verify email right now.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!password) {
      setError('Please enter your password.');
      return;
    }

    if (!accountExists && password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!accountExists && password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setSubmitting(true);
    try {
      if (accountExists) {
        await loginWithEmail(email, password);
      } else {
        await createAccountWithEmail(email, password, { email });
      }
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || 'Unable to sign in right now.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setSubmitting(true);
    try {
      await loginWithGoogle();
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || 'Google sign-in failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="auth-page">
      <header className="auth-header">
        <p className="auth-eyebrow">Your account</p>
        <h1 className="auth-title">{step === 'email' ? 'Welcome back' : accountExists ? 'Sign in' : 'Create account'}</h1>
        <p className="auth-subtitle">
          {step === 'email'
            ? 'Sign in or create an account to save your details and view order history.'
            : accountExists
              ? 'Enter your password to continue.'
              : 'Choose a password to secure your account.'}
        </p>
      </header>

      <div className="auth-card">
        {step === 'email' ? (
          <form className="auth-form" onSubmit={handleEmailContinue} noValidate>
            <div className="auth-field">
              <label htmlFor="auth-email" className="auth-label">Email Address</label>
              <input
                id="auth-email"
                type="email"
                autoComplete="email"
                className="auth-input"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                placeholder="you@example.com"
              />
            </div>

            {error && <p className="auth-error" role="alert">{error}</p>}

            <button type="submit" className="auth-btn auth-btn--primary" disabled={submitting}>
              {submitting ? 'Checking…' : 'Continue'}
            </button>

            <div className="auth-divider"><span>or</span></div>

            <button
              type="button"
              className="auth-btn auth-btn--google"
              onClick={handleGoogleSignIn}
              disabled={submitting}
            >
              Continue With Google
            </button>
          </form>
        ) : (
          <form className="auth-form" onSubmit={handlePasswordSubmit} noValidate>
            <p className="auth-email-readout">{email}</p>
            <button
              type="button"
              className="auth-link-btn"
              onClick={() => {
                setStep('email');
                setPassword('');
                setConfirmPassword('');
                setError('');
              }}
            >
              Use a different email
            </button>

            <div className="auth-field">
              <label htmlFor="auth-password" className="auth-label">Password</label>
              <input
                id="auth-password"
                type="password"
                autoComplete={accountExists ? 'current-password' : 'new-password'}
                className="auth-input"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </div>

            {!accountExists && (
              <div className="auth-field">
                <label htmlFor="auth-confirm-password" className="auth-label">Confirm Password</label>
                <input
                  id="auth-confirm-password"
                  type="password"
                  autoComplete="new-password"
                  className="auth-input"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  required
                />
              </div>
            )}

            {error && <p className="auth-error" role="alert">{error}</p>}

            <button type="submit" className="auth-btn auth-btn--primary" disabled={submitting}>
              {submitting ? 'Please wait…' : accountExists ? 'Sign in' : 'Create account'}
            </button>
          </form>
        )}

        <p className="auth-footnote">
          Guest checkout remains available. <Link to="/checkout">Continue as guest</Link>
        </p>
      </div>
    </main>
  );
};

export default AuthPage;
